import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { FormProvider, useForm, useWatch, UseFormReturn } from 'react-hook-form'

import classnames from 'classnames'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'

import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import useTracker from 'src/lib/js/hooks/useTracker'
import {
  TOAST_LOCAL_ACCOUNT_PATH,
  TOAST_LOCAL_PATH
} from 'src/public/js/siteUtilities'
import { getPath } from 'src/lib/js/utilities'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import {
  CAInfo,
  ToastTOSAndPrivacy
} from 'src/public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'
import Image from 'src/shared/components/common/Image'
import { InternationalPhoneInput } from 'src/shared/components/common/form_input/InternationalPhoneInput'
import { t, Trans } from 'src/translations'

import {
  ModalContent,
  useModalContext,
  ModalCloseButton
} from 'shared/components/common/modal'
import { alertSuccess } from 'shared/js/alertUtils'

import {
  CompleteProfileForm,
  CompleteProfileFormInputs
} from 'public/components/default_template/online_ordering/account/pwlessAuth/CompleteProfileForm'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import {
  ConfirmCodeForm,
  onCompleteProps
} from 'public/components/default_template/online_ordering/customer/ConfirmationCode'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { LockLockedIcon } from '@toasttab/buffet-pui-icons'
import Button from 'shared/components/common/button'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { LoadingSpinner } from 'src/shared/components/common/loading_spinner/LoadingSpinner'
import { PasswordlessAuthenticationErrorCode } from 'src/apollo/onlineOrdering'
import { GUEST_HELP_URL } from 'shared/components/common/authentication/constants'

type FormInputs = {
  yourInfoPhone: string
}

export enum LoginStage {
  EnterPhone = 'EnterPhone',
  EnterVerificationCode = 'EnterVerificationCode',
  CompleteProfile = 'CompleteProfile',
  LoggedOutAccountPage = 'LoggedOutAccountPage'
}

export type AccountCreationFormInputs = {
  email?: string
  firstName?: string
  lastName?: string
  phoneNumber: string
}

export const PwlessAuth = ({
  source,
  skipLoginToast,
  onSuccessfulLogin,
  contentClassname,
  currentLoginStage = LoginStage.EnterPhone,
  accountInfo,
  showBackButton = true,
  secondaryCTAText,
  secondaryContent,
  customLoginHeader,
  saveValuesForCheckout,
  onCloseAction,
  returnFocusElementId
}: {
  source: AuthenticationSource
  skipLoginToast?: boolean
  onSuccessfulLogin?: () => void
  contentClassname?: string
  currentLoginStage?: LoginStage
  accountInfo?: AccountCreationFormInputs
  showBackButton?: boolean
  secondaryCTAText?: string
  secondaryContent?: ReactNode
  customLoginHeader?: string
  saveValuesForCheckout?: (formInputs: CompleteProfileFormInputs) => void
  onCloseAction?: () => void
  // Where the focus trap returns focus on close, for triggers that do not
  // survive a successful login.
  returnFocusElementId?: string
}) => {
  const [loginStage, setLoginStage] = useState(currentLoginStage)
  const formMethods = useForm<FormInputs>({
    mode: 'onTouched',
    defaultValues: { yourInfoPhone: accountInfo?.phoneNumber || '' }
  })
  const phoneNumber = useWatch({
    name: 'yourInfoPhone',
    control: formMethods.control
  })
  const [showSecondaryContent, setShowSecondaryContent] = useState(false)
  const [canGoBack, setCanGoBack] = useState(false)
  const { checkoutPath } = useRestaurantRoutes()
  const isCheckingOut = useMemo(
    () =>
      typeof window !== 'undefined' &&
      checkoutPath === window.location.pathname,
    [checkoutPath]
  )
  const loginRequired = useMemo(
    () => currentLoginStage === LoginStage.LoggedOutAccountPage,
    [currentLoginStage]
  )
  const tracker = useTracker()
  const { onClose } = useModalContext()

  useEffect(() => {
    if (showSecondaryContent) {
      setCanGoBack(true)
    } else if (
      (loginStage === LoginStage.EnterPhone && !loginRequired) ||
      loginStage === LoginStage.LoggedOutAccountPage ||
      !showBackButton
    ) {
      setCanGoBack(false)
    } else {
      setCanGoBack(true)
    }
  }, [loginStage, showBackButton, showSecondaryContent, loginRequired])

  const handleClose = useCallback(() => {
    tracker.track(TrackingEvents.EXITED_2FA_FLOW_EARLY, { loginStage })
    onCloseAction?.()
    onClose()
  }, [onClose, tracker, loginStage, onCloseAction])

  return (
    <ModalContent
      contentClassName={classnames(
        'modalContentV2 authModal',
        loginStage === LoginStage.LoggedOutAccountPage
          ? 'bottomDrawerMobile'
          : 'fullScreenMobile',
        contentClassname,
        loginStage === LoginStage.CompleteProfile ? 'completeProfile' : '',
        secondaryCTAText ? 'secondaryCTA' : ''
      )}
      data-testid='PwlessAuth'
      ariaLabelledBy='pwless-auth-modal-header'
      returnFocusElementId={returnFocusElementId}
    >
      <div className='modalHeader' data-testid='PwlessAuth'>
        {canGoBack && (
          <button
            aria-label={t('pwless-auth.return-to-phone-input')}
            className='backButton'
            onClick={() => {
              if (showSecondaryContent) {
                setShowSecondaryContent(false)
              } else if (
                loginStage === LoginStage.EnterPhone &&
                loginRequired
              ) {
                setLoginStage(LoginStage.LoggedOutAccountPage)
                formMethods.reset({ yourInfoPhone: '' })
              } else {
                setLoginStage(LoginStage.EnterPhone)
                formMethods.reset({ yourInfoPhone: '' })
              }
            }}
          >
            <Image alt='' src='icons/arrow-left-gray.svg' />
          </button>
        )}
        <Image src='icons/toast-logo-orange-with-text.svg' alt='Toast logo' />
        {!loginRequired && <ModalCloseButton onClose={handleClose} />}
      </div>
      {showSecondaryContent ? (
        secondaryContent
      ) : (
        <div
          className={classnames('body', {
            extraBottomPadding:
              !isCheckingOut && loginStage === LoginStage.EnterPhone
          })}
        >
          {loginStage === LoginStage.CompleteProfile ? (
            <CompleteProfileForm
              accountInfo={accountInfo ?? { phoneNumber }}
              onSuccessfulLogin={onSuccessfulLogin}
              skipLoginToast={skipLoginToast}
              setCanGoBack={setCanGoBack}
              saveValuesForCheckout={saveValuesForCheckout}
              handleClose={handleClose}
              checkingOut={isCheckingOut}
            />
          ) : loginStage === LoginStage.LoggedOutAccountPage ? (
            <LoginRequiredForm
              onContinue={() => setLoginStage(LoginStage.EnterPhone)}
            />
          ) : (
            <LoginForm
              formMethods={formMethods}
              loginStage={loginStage}
              setLoginStage={setLoginStage}
              source={source}
              onSuccessfulLogin={onSuccessfulLogin}
              customHeader={customLoginHeader}
              secondaryCTA={
                secondaryCTAText ? (
                  <button
                    type='button'
                    className='cta secondary'
                    onClick={() => setShowSecondaryContent(true)}
                  >
                    <div className='text'>{secondaryCTAText}</div>
                  </button>
                ) : undefined
              }
              skipLoginToast={skipLoginToast ?? false}
              isCheckingOut={isCheckingOut}
              handleClose={handleClose}
            />
          )}
        </div>
      )}
      {loginStage !== LoginStage.CompleteProfile && (
        <div className='safeAndSecure'>
          <LockLockedIcon accessibility='decorative' size='xs' />
          {t('pwless-auth.safe-and-secure')}
        </div>
      )}
    </ModalContent>
  )
}

const LoginRequiredForm = ({ onContinue }: { onContinue: () => void }) => {
  const { orderPath } = useRestaurantRoutes()
  const [loading, setLoading] = useState(false)
  const isToastLocalAccountPage = useMemo(
    () => getPath() === TOAST_LOCAL_ACCOUNT_PATH,
    []
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.onpageshow = (event) => {
        if (event.persisted && loading) {
          setLoading(false)
        }
      }
    }
  }, [setLoading, loading])

  return (
    <div className='loggedOut'>
      <div className='content'>
        <div className='headerText'>{t('pwless-auth.not-logged-in')}</div>
        <div className='subheaderText'>
          {t('pwless-auth.please-continue-to-log-in-or-create-account')}
        </div>
      </div>
      <div className='actions'>
        <button onClick={onContinue}>
          {t('pwless-auth.continue-to-log-in')}
        </button>
        <a
          href={isToastLocalAccountPage ? TOAST_LOCAL_PATH : orderPath}
          onClick={() => setLoading(true)}
          className='cancelButton'
        >
          {loading ? (
            <LoadingSpinner size='21px' strokeWidth='4px' />
          ) : isToastLocalAccountPage ? (
            t('pwless-auth.go-back-home')
          ) : (
            t('pwless-auth.go-back-to-menu')
          )}
        </a>
      </div>
    </div>
  )
}

const LoginForm = ({
  formMethods,
  loginStage,
  setLoginStage,
  source,
  skipLoginToast = false,
  onSuccessfulLogin,
  secondaryCTA,
  customHeader,
  isCheckingOut,
  handleClose
}: {
  formMethods: UseFormReturn<FormInputs>
  loginStage: LoginStage
  setLoginStage: (loginStage: LoginStage) => void
  source: AuthenticationSource
  skipLoginToast: boolean
  onSuccessfulLogin?: () => void
  secondaryCTA?: ReactNode
  customHeader?: string
  isCheckingOut: boolean
  handleClose: () => void
}) => {
  const { onClose } = useModalContext()
  const {
    customer,
    passwordlessLogin,
    fetchCustomer,
    completeSignup,
    passwordlessConfirmCode
  } = useCustomer()
  const [error, setError] = useState('')
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [verificationLoading, setVerificationLoading] = useState(false)
  const tracker = useTracker()
  const isIntlRestaurant = useIsIntlRestaurant()
  const phoneNumber = useWatch({
    name: 'yourInfoPhone',
    control: formMethods.control
  })
  const parsedPhoneNumber = useMemo(
    () =>
      isValidPhoneNumber(phoneNumber) ? parsePhoneNumber(phoneNumber) : null,
    [phoneNumber]
  )

  const handlePhoneInputKeydown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      await onSubmitPhone()
      event.stopPropagation()
      event.preventDefault()
    }
  }

  const onSubmitPhone = useCallback(async () => {
    if (!parsedPhoneNumber) return

    // If it is a valid local phone number in the country of the rx, send the code
    tracker.track(TrackingEvents.CLICKED_SEND_CODE, {
      source,
      countryCode: parsedPhoneNumber.country
    })

    setVerificationLoading(true)
    const result = await passwordlessLogin(parsedPhoneNumber.number)
    setVerificationLoading(false)

    if (result.success) {
      setError('')
      setErrorCode(null)
      setLoginStage(LoginStage.EnterVerificationCode)
    } else {
      setErrorCode(result.errorCode ?? null)
      // Always use localized error messages, never pass through BFF messages
      setError(t('confirmation-code.error-sending-code'))
    }
  }, [parsedPhoneNumber, tracker, source, passwordlessLogin, setLoginStage])

  const onLogin = useCallback(async () => {
    const fetchedCustomer = await fetchCustomer()
    if (!fetchedCustomer) {
      setLoginStage(LoginStage.CompleteProfile)
    } else {
      onSuccessfulLogin?.()
      onClose()
      if (!skipLoginToast)
        alertSuccess(
          fetchedCustomer?.firstName
            ? `Welcome back, ${fetchedCustomer?.firstName}!`
            : 'Welcome back!',
          {
            icon: <Image src='/icons/toast-logo-filled.svg' alt='Toast logo' />
          }
        )
      tracker.track(TrackingEvents.COMPLETED_2FA, {
        source,
        newOrExistingAccount: 'existing account'
      })
      await completeSignup(
        fetchedCustomer?.email || '',
        fetchedCustomer?.firstName || '',
        fetchedCustomer?.lastName || ''
      )
    }
  }, [
    fetchCustomer,
    setLoginStage,
    onSuccessfulLogin,
    onClose,
    skipLoginToast,
    tracker,
    source,
    completeSignup
  ])

  const onConfirmCodeComplete = useCallback(
    async ({ identity, code, setError }: onCompleteProps) => {
      const response = await passwordlessConfirmCode(identity, code)
      if (response) {
        await onLogin()
      } else {
        setError('The code you entered was incorrect. Please try again.')
      }
    },
    [onLogin, passwordlessConfirmCode]
  )

  useEffect(() => {
    // This resolves an issue where Android keyboard was overlaying input
    const phoneInput = document.getElementById('yourInfoPhonePwlessAuth')
    phoneInput?.addEventListener('focus', () => {
      if (phoneInput && typeof phoneInput.scrollIntoView === 'function') {
        setTimeout(() => phoneInput.scrollIntoView(), 100) // Small delay to allow keyboard to render
      }
    })
  }, [])

  const onClickChangeNumber = useCallback(() => {
    setLoginStage(LoginStage.EnterPhone)
    formMethods.reset({ yourInfoPhone: '' })
  }, [setLoginStage, formMethods])

  return (
    <FormProvider {...formMethods}>
      <div className='headerContent centered'>
        <h2 className='title' id='pwless-auth-modal-header'>
          {customHeader ??
            (loginStage === LoginStage.EnterVerificationCode
              ? t('pwless-auth.enter-verification')
              : t('pwless-auth.enter-your-phone-number'))}
        </h2>
        <p className='subtitle'>
          {loginStage === LoginStage.EnterVerificationCode ? (
            <Trans
              i18nKey='pwless-auth.code-sent-to'
              components={{
                ChangeNumber: (
                  <span
                    className='changeNumberButton'
                    role='button'
                    tabIndex={0}
                    onClick={onClickChangeNumber}
                    onKeyDown={(e) => {
                      if (
                        e.key === ' ' ||
                        e.key === 'Spacebar' ||
                        e.key === 'Enter'
                      ) {
                        e.preventDefault()
                        onClickChangeNumber()
                      }
                    }}
                  />
                )
              }}
              values={{
                phoneNumber: parsedPhoneNumber
                  ? parsedPhoneNumber.formatNational()
                  : phoneNumber
              }}
            />
          ) : (
            t('pwless-auth.well-send-you-a-code')
          )}
        </p>
      </div>
      <div id='pwlessauthcontent'>
        {loginStage === LoginStage.EnterVerificationCode &&
        parsedPhoneNumber ? (
          <ConfirmCodeForm
            identity={parsedPhoneNumber.number}
            onComplete={onConfirmCodeComplete}
            sendCode={onSubmitPhone}
            loading={verificationLoading}
          />
        ) : (
          <div className='phoneForm'>
            <InternationalPhoneInput
              id='yourInfoPhonePwlessAuth'
              // We specify a different name here to avoid scroll conflicts with other inputs
              name='yourInfoPhone'
              required
              label={t('pwless-auth.phone-number')}
              value={customer?.phone || ''}
              warning={
                errorCode ===
                PasswordlessAuthenticationErrorCode.DeactivatedPhone
                  ? ''
                  : error
              }
              unregisterOnUnmount={false}
              onKeyDown={handlePhoneInputKeydown}
              shouldUnFocus={false}
              initialFocus={true}
              isSingletonField={true}
            />
            {errorCode ===
              PasswordlessAuthenticationErrorCode.DeactivatedPhone &&
              error && (
                <div className='errorMessage' role='alert'>
                  <Trans
                    i18nKey='confirmation-code.deactivated-phone-error'
                    components={{
                      GuestHelpLink: (
                        <a
                          href={GUEST_HELP_URL}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {/* content */}
                        </a>
                      )
                    }}
                  />
                </div>
              )}
            <Button
              className='submitBtn'
              onClick={onSubmitPhone}
              disabled={!parsedPhoneNumber}
            >
              {verificationLoading ? (
                <LoadingSpinner size='24' strokeWidth='3' color='#FFFFFF' />
              ) : (
                t('pwless-auth.continue')
              )}
            </Button>
            {secondaryCTA}
          </div>
        )}
      </div>
      <p className='legal' role='contentinfo'>
        {getLegalText(isIntlRestaurant, loginStage)}
      </p>
      {isCheckingOut && (
        <div className='checkoutAsGuestFooter'>
          <div className='orSeparator'>
            <div className='separator' />
            <div>{t('pwless-auth.or')}</div>
            <div className='separator' />
          </div>
          <button className='checkoutAsGuestButton' onClick={handleClose}>
            {t('pwless-auth.checkout-as-guest')}
          </button>
        </div>
      )}
    </FormProvider>
  )
}

const getLegalText = (isIntlRestaurant: boolean, loginStage: LoginStage) => {
  return loginStage === LoginStage.EnterPhone ? (
    <>
      <Trans
        i18nKey={'pwless-auth.legal-phone'}
        components={{
          ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
          CAInfo: !isIntlRestaurant ? <CAInfo /> : <></>
        }}
      />
    </>
  ) : (
    <>
      <Trans
        i18nKey={'pwless-auth.legal-continue'}
        components={{
          ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
          CAInfo: !isIntlRestaurant ? <CAInfo /> : <></>
        }}
      />
    </>
  )
}
